import React, { useState } from "react";

import {
  MdmObjectDto,
  MdmObjectJobDto,
  useDeleteJobMutation,
  useGetJobsByObjectIdQuery,
  useGetJobsByProjectIdQuery,
} from "gql/generated/types";
import { useDisplayTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { DATE_PATTERN_NO_TIME } from "utils/constants";
import { fmtDatetime } from "utils/time-utils";
import { MdmTableName, TableColumn } from "../utils/types";
import {
  apolloErrorToaster,
  renderSeverity,
  renderTag,
  severityRowFilterTemplate,
  statusRowFilterTemplate,
  successToaster,
} from "../utils/utils";
import ItemsTable from "./ItemsTable";
import JobForm from "./JobForm";

type ObjectJobsProps = {
  object?: MdmObjectDto;
  projectId?: string;
  showToolbarBtn?: boolean;
  tableName: MdmTableName;
};

const ObjectJobs: React.FC<ObjectJobsProps> = ({ object, projectId, tableName, showToolbarBtn = true }) => {
  const [open, setOpen] = useState(false);
  const [job, setJob] = useState<MdmObjectJobDto | null>(null);

  const displayTz = useDisplayTimezone();

  const { data, loading } = useGetJobsByObjectIdQuery({
    variables: {
      id: object?.id!,
    },
    skip: !object?.id,
  });

  const { data: jobsData, loading: jobsLoading } = useGetJobsByProjectIdQuery({
    variables: {
      id: projectId!,
    },
    skip: !projectId,
  });

  const jobs = data?.allJobsByObjectId ?? jobsData?.allJobsByProjectId ?? [];
  const isLoading = loading || jobsLoading;

  const [deleteJobMutation] = useDeleteJobMutation({
    onCompleted: () => successToaster(T("alert.job-deleted-successfully")),
    onError: apolloErrorToaster,
  });

  const renderActions = (rowData: MdmObjectJobDto) => (
    <>
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text h-2rem w-2rem"
        onClick={() => handleEditClick(rowData)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-text p-button-danger h-2rem w-2rem"
        onClick={e => handleDeleteJob(rowData, e)}
      />
    </>
  );

  const handleEditClick = (rowData: MdmObjectJobDto) => {
    setJob(rowData);
    setOpen(true);
  };

  const handleDeleteJob = (jobData: MdmObjectJobDto, event: React.MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: event?.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteJobMutation({
          variables: {
            jobId: jobData.id,
          },
          update(cache) {
            const normalizedId = cache.identify({ id: jobData.id, __typename: "MdmObjectJobDTO" });
            cache.evict({ id: normalizedId });
            cache.gc();
            cache.modify({
              id: cache.identify({ id: object?.id, __typename: "MdmObjectDTO" }),
              fields: {
                info: info => {
                  return {
                    ...info,
                    jobsCount: info.jobsCount - 1,
                  };
                },
              },
            });
          },
        });
      },
    });
  };

  const getObject = (rowData: MdmObjectJobDto) => (
    <Link
      to={`/mdm/${projectId}/objects/${rowData.mdmObject?.id}`}
      className="truncate-text block"
      title={rowData.mdmObject?.title ?? ""}
    >
      {rowData.mdmObject?.title}
    </Link>
  );

  const columns: TableColumn<MdmObjectJobDto>[] = [
    {
      field: "startDate",
      header: T("word.start-date"),
      body: rowData => fmtDatetime(rowData?.startDate, displayTz, DATE_PATTERN_NO_TIME),
      sortable: true,
      filter: true,
      columnKey: "startDate",
    },
    {
      field: "endDate",
      header: T("word.end-date"),
      body: rowData => fmtDatetime(rowData?.endDate, displayTz, DATE_PATTERN_NO_TIME),
      sortable: true,
      filter: true,
      columnKey: "endDate",
    },
    {
      field: "createdByUsername",
      header: T("word.created-by"),
      filter: true,
      sortable: true,
      columnKey: "createdByUsername",
    },
    {
      field: "severity",
      header: T("word.priority"),
      filter: true,
      body: renderSeverity,
      filterElement: severityRowFilterTemplate,
      filterMatchMode: "equals",
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
      sortable: true,
      columnKey: "severity",
    },
    {
      field: "status",
      header: T("word.status"),
      body: renderTag,
      sortable: true,
      filter: true,
      filterElement: statusRowFilterTemplate,
      filterMatchMode: "equals",
      showFilterMenuOptions: false,
      showApplyButton: false,
      showClearButton: false,
      columnKey: "status",
    },
    { field: "title", header: T("word.title"), columnKey: "title", sortable: true },
    {
      field: "description",
      header: T("word.description"),
      style: { maxWidth: "32rem" },
      sortable: true,
      columnKey: "description",
    },
    projectId
      ? {
          field: "mdmObject.title",
          header: T("word.object"),
          body: getObject,
          columnKey: "objectTitle",
          sortable: true,
        }
      : { header: T("word.actions"), body: renderActions, hideInFile: true, columnKey: "actions" },
  ];

  const handleCloseDialog = () => {
    setJob(null);
    setOpen(false);
  };

  return (
    <>
      <ItemsTable
        data={jobs as MdmObjectJobDto[]}
        loading={isLoading}
        showToolbarBtn={showToolbarBtn}
        columns={columns}
        tableName={tableName}
        onToolbarBtnClick={() => setOpen(true)}
        searchFields={["description", "createdBy", "startDate", "endDate", "title", "severity"]}
      />
      <Dialog
        header={job ? T("word.edit-job") : T("word.create-job")}
        visible={open}
        onHide={handleCloseDialog}
        className="w-6"
      >
        <JobForm object={object!} closeDialog={handleCloseDialog} job={job} />
      </Dialog>
    </>
  );
};

export default ObjectJobs;
